import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Campaign } from '@/services/campaignService';
import { replaceMergeFields, getExampleVariables } from '@/lib/campaignMergeFields';
import { fetchCampaignQueuePreviews, CampaignQueuePreview } from '@/services/campaignQueueService';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}

function isMailMergeCampaign(campaign: Campaign): boolean {
  const html = campaign.html_content?.trim().toLowerCase() || '';
  return !html || html.includes('personalized per recipient');
}

const CampaignPreviewDialog: React.FC<Props> = ({ open, onOpenChange, campaign }) => {
  const mailMerge = isMailMergeCampaign(campaign);
  const [previews, setPreviews] = useState<CampaignQueuePreview[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (open && mailMerge) {
      setLoading(true);
      setSelectedIndex(0);
      fetchCampaignQueuePreviews(campaign.id)
        .then(setPreviews)
        .catch(() => setPreviews([]))
        .finally(() => setLoading(false));
    }
  }, [open, campaign.id, mailMerge]);

  // Normal campaign rendering
  if (!mailMerge) {
    const vars = getExampleVariables();
    const renderedSubject = replaceMergeFields(campaign.subject, vars);
    const renderedHtml = replaceMergeFields(campaign.html_content, vars);

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
<DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview: {campaign.name}</DialogTitle>
          </DialogHeader>
          <div className="mb-3">
            <div className="text-sm text-muted-foreground">Subject</div>
            <div className="font-medium">{renderedSubject}</div>
          </div>
          <Tabs defaultValue="rendered">
            <TabsList>
              <TabsTrigger value="rendered">Rendered</TabsTrigger>
              <TabsTrigger value="source">HTML Source</TabsTrigger>
              <TabsTrigger value="text">Plain Text</TabsTrigger>
            </TabsList>
            <TabsContent value="rendered" className="border rounded-lg p-4 mt-2 bg-white text-gray-900">
              <div dangerouslySetInnerHTML={{ __html: renderedHtml }} />
            </TabsContent>
            <TabsContent value="source" className="mt-2">
              <pre className="bg-muted p-4 rounded-lg text-xs overflow-auto max-h-[400px]">{campaign.html_content}</pre>
            </TabsContent>
            <TabsContent value="text" className="mt-2">
              <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap">{campaign.text_content || 'No plain text version.'}</pre>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    );
  }

  // Mail merge campaign rendering
  const current = previews[selectedIndex];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preview: {campaign.name}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading previews…</span>
          </div>
        ) : previews.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No recipient emails found for this campaign.
          </div>
        ) : (
          <>
            {/* Recipient selector */}
            <div className="flex items-center gap-2 mb-4">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                disabled={selectedIndex === 0}
                onClick={() => setSelectedIndex(i => i - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <Select
                value={String(selectedIndex)}
                onValueChange={(v) => setSelectedIndex(Number(v))}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {previews.map((p, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {p.recipient_name ? `${p.recipient_name} (${p.recipient_email})` : p.recipient_email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                disabled={selectedIndex === previews.length - 1}
                onClick={() => setSelectedIndex(i => i + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {selectedIndex + 1} of {previews.length}
              </span>
            </div>

            {/* Subject */}
            <div className="mb-3">
              <div className="text-sm text-muted-foreground">Subject</div>
              <div className="font-medium">{current.subject}</div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="rendered">
              <TabsList>
                <TabsTrigger value="rendered">Rendered</TabsTrigger>
                <TabsTrigger value="source">HTML Source</TabsTrigger>
                <TabsTrigger value="text">Plain Text</TabsTrigger>
              </TabsList>
              <TabsContent value="rendered" className="border rounded-lg p-4 mt-2 bg-white text-gray-900">
                <div dangerouslySetInnerHTML={{ __html: current.html_content || '' }} />
              </TabsContent>
              <TabsContent value="source" className="mt-2">
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-auto max-h-[400px]">{current.html_content || 'No HTML content.'}</pre>
              </TabsContent>
              <TabsContent value="text" className="mt-2">
                <pre className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap">{current.text_content || 'No plain text version.'}</pre>
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CampaignPreviewDialog;
